import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Edit2, Trash2, X, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { absoluteUrl } from "@/components/PinItButton";
import { toast } from "sonner";

const emptyProduct = {
  name: "", description: "", image_url: "", affiliate_url: "",
  category: "edc", price: "", tags: [],
  featured: false, trending: false, best_of_week: false,
};
const emptyPost = {
  title: "", slug: "", excerpt: "", content: "",
  cover_image: "", author: "Gulf Coast Home Co.", tags: [],
};

export default function AdminDashboardPage() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(null); // {type, item}

  const reload = () => {
    api.get("/products?limit=200").then(({ data }) => setProducts(data));
    api.get("/blog?limit=200").then(({ data }) => setPosts(data));
    api.get("/categories").then(({ data }) => setCategories(data));
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    reload();
  }, []);

  if (loading) return <div className="p-12 text-[#636769]">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const onDelete = async (kind, id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      if (kind === "product") await api.delete(`/admin/products/${id}`);
      else await api.delete(`/admin/blog/${id}`);
      toast.success("Deleted.");
      reload();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-2">Admin</p>
          <h1 className="font-heading text-3xl text-[#2D3032]">Content Studio</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="tab-products" onClick={() => setTab("products")} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border ${tab === "products" ? "bg-[#2D3032] text-white border-[#2D3032]" : "border-[#E6E1D6] text-[#636769]"}`}>Products</button>
          <button data-testid="tab-blog" onClick={() => setTab("blog")} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border ${tab === "blog" ? "bg-[#2D3032] text-white border-[#2D3032]" : "border-[#E6E1D6] text-[#636769]"}`}>Blog</button>
          <button data-testid="tab-collections" onClick={() => setTab("collections")} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border ${tab === "collections" ? "bg-[#2D3032] text-white border-[#2D3032]" : "border-[#E6E1D6] text-[#636769]"}`}>Collections</button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        {tab !== "collections" && (
          <button
            data-testid="add-new-btn"
            onClick={() => setModal({ type: tab, item: tab === "products" ? { ...emptyProduct } : { ...emptyPost } })}
            className="inline-flex items-center gap-2 bg-[#EBAA86] text-[#2D3032] px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#2D3032] hover:text-white transition-colors"
          >
            <Plus size={14} /> New {tab === "products" ? "Product" : "Post"}
          </button>
        )}
        {tab !== "collections" && (
          <button
            data-testid="reset-content-btn"
            onClick={async () => {
              if (!window.confirm("Delete ALL products and blog posts? This cannot be undone.")) return;
              try {
                const { data } = await api.post("/admin/reset-content");
                toast.success(`Cleared: ${data.products_deleted} products, ${data.posts_deleted} posts.`);
                reload();
              } catch (e) {
                toast.error("Reset failed.");
              }
            }}
            className="inline-flex items-center gap-2 border border-[#E6E1D6] text-[#636769] px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-medium hover:border-red-500 hover:text-red-600 transition-colors"
          >
            <Trash2 size={14} /> Reset demo content
          </button>
        )}
      </div>

      {tab === "products" && (
        <div className="bg-white border border-[#E6E1D6]" data-testid="products-table">
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-4 border-b border-[#E6E1D6] last:border-0">
              <img src={absoluteUrl(p.image_url)} alt="" className="w-14 h-14 object-cover bg-[#F4F1EA]" />
              <div className="flex-1 min-w-0">
                <p className="text-[#2D3032] font-medium truncate">{p.name}</p>
                <p className="text-xs text-[#636769] uppercase tracking-wider">{p.category} {p.price && `· ${p.price}`}</p>
              </div>
              <button data-testid={`edit-product-${p.id}`} onClick={() => setModal({ type: "products", item: p })} className="text-[#636769] hover:text-[#2D3032] p-2"><Edit2 size={16} /></button>
              <button data-testid={`delete-product-${p.id}`} onClick={() => onDelete("product", p.id)} className="text-[#636769] hover:text-red-600 p-2"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {tab === "blog" && (
        <div className="bg-white border border-[#E6E1D6]" data-testid="posts-table">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-4 border-b border-[#E6E1D6] last:border-0">
              <img src={absoluteUrl(p.cover_image)} alt="" className="w-14 h-14 object-cover bg-[#F4F1EA]" />
              <div className="flex-1 min-w-0">
                <p className="text-[#2D3032] font-medium truncate">{p.title}</p>
                <p className="text-xs text-[#636769] truncate">/{p.slug}</p>
              </div>
              <button data-testid={`edit-post-${p.id}`} onClick={() => setModal({ type: "blog", item: p })} className="text-[#636769] hover:text-[#2D3032] p-2"><Edit2 size={16} /></button>
              <button data-testid={`delete-post-${p.id}`} onClick={() => onDelete("post", p.id)} className="text-[#636769] hover:text-red-600 p-2"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {tab === "collections" && (
        <CollectionsPanel categories={categories} onChange={reload} />
      )}

      {modal && (
        <Modal
          modal={modal}
          categories={categories}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); reload(); }}
        />
      )}
    </div>
  );
}

function CollectionsPanel({ categories, onChange }) {
  const [uploadingSlug, setUploadingSlug] = useState(null);

  const upload = async (slug, file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploadingSlug(slug);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put(`/admin/categories/${slug}/image`, { image_url: data.url });
      toast.success("Category image updated.");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingSlug(null);
    }
  };

  const resetImg = async (slug) => {
    if (!window.confirm("Reset to the default image for this collection?")) return;
    try {
      await api.delete(`/admin/categories/${slug}/image`);
      toast.success("Reset to default.");
      onChange();
    } catch {
      toast.error("Reset failed.");
    }
  };

  return (
    <div className="bg-white border border-[#E6E1D6]" data-testid="collections-table">
      <p className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-[#636769] border-b border-[#E6E1D6]">
        Upload a photo for any collection card — it instantly updates the homepage grid.
      </p>
      {categories.map((c) => (
        <div key={c.slug} className="flex items-center gap-4 p-4 border-b border-[#E6E1D6] last:border-0">
          <img src={absoluteUrl(c.image)} alt="" className="w-20 h-28 object-cover bg-[#F4F1EA]" />
          <div className="flex-1 min-w-0">
            <p className="text-[#2D3032] font-medium truncate">{c.name}</p>
            <p className="text-xs text-[#636769] truncate">/{c.slug}</p>
            {c.pinterest_board && (
              <a href={c.pinterest_board} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#EBAA86] uppercase tracking-[0.18em] hover:text-[#2D3032]">View Pinterest board →</a>
            )}
          </div>
          <label className="inline-flex items-center gap-1.5 border border-[#2D3032] text-[#2D3032] px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-medium hover:bg-[#2D3032] hover:text-white transition-colors cursor-pointer">
            <Upload size={12} />
            {uploadingSlug === c.slug ? "..." : "Upload"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid={`upload-cat-${c.slug}`}
              onChange={(e) => e.target.files?.[0] && upload(c.slug, e.target.files[0])}
            />
          </label>
          <button
            data-testid={`reset-cat-${c.slug}`}
            onClick={() => resetImg(c.slug)}
            className="text-[#636769] hover:text-red-600 p-2"
            title="Reset to default"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

function Modal({ modal, categories, onClose, onSaved }) {
  const [form, setForm] = useState(modal.item);
  const [saving, setSaving] = useState(false);
  const isEdit = !!form.id;
  const isProduct = modal.type === "products";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isProduct) {
        const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags };
        if (isEdit) await api.put(`/admin/products/${form.id}`, payload);
        else await api.post("/admin/products", payload);
      } else {
        const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags };
        if (isEdit) await api.put(`/admin/blog/${form.id}`, payload);
        else await api.post("/admin/blog", payload);
      }
      toast.success("Saved.");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, name, type = "text", ...rest }) => (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-[#636769] block mb-1">{label}</label>
      <input type={type} value={form[name] ?? ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="w-full border border-[#E6E1D6] px-3 py-2 outline-none focus:border-[#2D3032] bg-white" {...rest} />
    </div>
  );

  const ImageField = ({ label, name, required }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const upload = async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      setUploading(true);
      try {
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm({ ...form, [name]: data.url });
        toast.success("Image uploaded.");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Upload failed.");
      } finally {
        setUploading(false);
      }
    };

    return (
      <div data-testid={`image-field-${name}`}>
        <label className="text-[10px] uppercase tracking-[0.22em] text-[#636769] block mb-1">{label}</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={form[name] ?? ""}
            placeholder="https://… or upload below"
            onChange={(e) => setForm({ ...form, [name]: e.target.value })}
            className="flex-1 border border-[#E6E1D6] px-3 py-2 outline-none focus:border-[#2D3032] bg-white"
            required={required}
          />
          <button
            type="button"
            data-testid={`upload-${name}-btn`}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 border border-[#2D3032] text-[#2D3032] px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-medium hover:bg-[#2D3032] hover:text-white transition-colors disabled:opacity-50"
          >
            <Upload size={12} />
            {uploading ? "..." : "Upload"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            data-testid={`file-input-${name}`}
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            className="hidden"
          />
        </div>
        {form[name] && (
          <img src={absoluteUrl(form[name])} alt="preview" className="mt-2 h-20 w-auto object-cover border border-[#E6E1D6]" />
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="admin-modal">
      <form onSubmit={save} className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#E6E1D6]">
          <h3 className="font-heading text-lg text-[#2D3032]">{isEdit ? "Edit" : "New"} {isProduct ? "Product" : "Post"}</h3>
          <button type="button" onClick={onClose} data-testid="modal-close-btn"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {isProduct ? (
            <>
              <Field label="Name" name="name" required />
              <Field label="Description" name="description" required />
              <ImageField label="Image" name="image_url" required />
              <Field label="Affiliate URL" name="affiliate_url" required />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#636769] block mb-1">Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-[#E6E1D6] px-3 py-2 bg-white outline-none focus:border-[#2D3032]">
                    {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <Field label="Price" name="price" placeholder="$95" />
              </div>
              <Field label="Tags (comma separated)" name="tags" />
              <div className="flex gap-4 flex-wrap">
                {["featured", "trending", "best_of_week"].map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} />
                    <span className="capitalize">{k.replace("_", " ")}</span>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <>
              <Field label="Title" name="title" required />
              <Field label="Slug" name="slug" required placeholder="my-post-slug" />
              <Field label="Excerpt" name="excerpt" required />
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#636769] block mb-1">Content</label>
                <textarea rows={8} value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full border border-[#E6E1D6] px-3 py-2 outline-none focus:border-[#2D3032] bg-white" required />
              </div>
              <ImageField label="Cover Image" name="cover_image" required />
              <Field label="Author" name="author" />
              <Field label="Tags (comma separated)" name="tags" />
            </>
          )}
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-[#E6E1D6]">
          <button type="button" onClick={onClose} className="px-5 py-2 text-xs uppercase tracking-[0.2em] border border-[#E6E1D6] text-[#636769]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="modal-save-btn" className="px-5 py-2 text-xs uppercase tracking-[0.2em] bg-[#2D3032] text-white disabled:opacity-50">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
